import { useEffect, useRef, useState } from 'react';
import PlanetEditor, { EditorTool, EditorTools, BrushChannel } from 'scripts/scenes/planet-editor';
import { Model, ModelPacks } from 'scripts/model-loader';
import { naturalSort } from 'scripts/utility';
import { useCallback } from 'react';
import TemplatePlanetModal, { TemplatePlanet } from 'components/modals/TemplatePlanetModal';
import GeoPlanet from 'scripts/objects/geo-planet';
import Engine from 'scripts/engine/engine';
import templates from 'scripts/objects/geo-planet/templates';

interface ToolbarProps {
    editor: PlanetEditor
} 

const defaults = {
    color: 0,
    tool: undefined,
}

const templatePlanets: TemplatePlanet[] = [
    {
        title: "Terra Nova",
        icon: 'earth-ico',
        data: templates.default
    },
    {
        title: "Frozen",
        icon: 'earth-ico',
        data: templates.frozen
    },
    {
        title: "Lava",
        icon: 'earth-ico',
        data: templates.lava
    },
    {
        title: "Empty",
        icon: 'empty-ico',
        data: templates.empty
    },
]

function Toolbar(props: ToolbarProps) {
    const { editor } = props;
    const [hideToolSettings, setHideToolSettings] = useState<boolean>(false);
    const [undoRedoCount, setUndoRedoCount] = useState<number[]>([0,0]);
    const [selectedTool, setSelectedTool] = useState<EditorTool | undefined>(defaults.tool);
    const [selectedSize, setSelectedSize] = useState<number>(0.5);
    const [selectedObject, setSelectedObject] = useState<Model | undefined>(undefined);
    const [selectedColor, setSelectedColor] = useState<number>(0);
    const [waterLevel, setWaterLevel] = useState<number>(16);
    const [waterDensity, setWaterDensity] = useState<number>(0);
    const [waterColor, setWaterColor] = useState<string>();
    const [atmosphereLevel, setAtmosphereLevel] = useState<number>(32);
    const [atmosphereDensity, setAtmosphereDensity] = useState<number>(0);
    const [atmosphereColor, setAtmosphereColor] = useState<string>();
    const [colorPalette, setColorPalette] = useState<string[]>([]);
    const brushMap = useRef<Map<BrushChannel, number>>(new Map());
    const [templateModalOpen, setTemplateModalOpen] = useState<boolean>(false);
    const inputFile = useRef<any>();

    const resetTools = useCallback(() => {
        setColorPalette(editor.planet.colorPalette.colors.map(c => `#${c.getHexString()}`));
        setWaterLevel(editor.planet.water.height);
        setWaterDensity(editor.planet.water.density);
        setWaterColor(`#${editor.planet.water.color.getHexString()}`);
        setAtmosphereLevel(editor.planet.atmosphere.height);
        setAtmosphereDensity(editor.planet.atmosphere.density);
        setAtmosphereColor(`#${editor.planet.atmosphere.color.getHexString()}`);
    }, [editor])

    useEffect(() => {
        resetTools();
        editor.axes.visible = false;
        editor.setBrushSize(0.5);
        editor.history.onUndoRedoChange.addListener('TOOLBAR', (undos: number, redos: number) => setUndoRedoCount([undos, redos]));
        editor.planet.colorPalette.onAfterChange.addListener('TOOLBAR', (colors: THREE.Color[]) => {
            setColorPalette(colors.map(c => `#${c.getHexString()}`));
        });
        editor.onUpdateCallback.addListener('TOOLBAR', () => {
            resetTools();
        });
        editor.onBrushSizeUpdateCallback.addListener('TOOLBAR', (size: number) => {
            setSelectedSize(size);
        });

        return () => {
            editor.onUpdateCallback.removeListener('TOOLBAR');
        }
    }, [editor, resetTools])

    const onSetSelectedTool = (tool: EditorTool) => {
        if (selectedTool === tool) {
            setSelectedTool(undefined);
            editor.tool = undefined;
        } else {
            setHideToolSettings(false);
            setSelectedTool(tool);
            editor.tool = tool;
        }
    }

    const onSetSelectedSize = (size: string) => {
        const numSize = parseFloat(size);
        if (selectedTool) {
            brushMap.current.set(selectedTool.brushChannel, numSize);
        }
        setSelectedSize(numSize);
        editor.setBrushSize(numSize);
    }

    const onSetSelectedColor = (color: number) => {
        setSelectedColor(color);
        editor.color = color;
    }

    const onSetWaterLevel = (level: string) => {
        const newWaterLevel = parseInt(level);
        setWaterLevel(newWaterLevel);
        editor.planet.water.setHeight(newWaterLevel);
    }

    const onSetWaterColor = (color: string) => {
        setWaterColor(color);
        editor.planet.water.setColor(color);
    }

    const onSetWaterDensity = (density: string) => {
        const newWaterDensity = parseInt(density);
        setWaterDensity(newWaterDensity);
        editor.planet.water.setDensity(newWaterDensity);
    }

    const onSetAtmosphereLevel = (level: string) => {
        const newAtmosphereLevel = parseInt(level);
        setAtmosphereLevel(newAtmosphereLevel);
        editor.planet.atmosphere.setHeight(newAtmosphereLevel);
    }

    const onSeAtmosphereDensity = (density: string) => {
        const newAtmosphereDensity = parseInt(density);
        setAtmosphereDensity(newAtmosphereDensity);
        editor.planet.atmosphere.setDensity(newAtmosphereDensity);
    }

    const onSetAtmosphereColor = (color: string) => {
        setAtmosphereColor(color);
        editor.planet.atmosphere.setColor(color);
    }

    const onSetSelectedObject = (model: Model) => {
        setSelectedObject(model);
        editor.model = model;
    }

    const onExportPlanet = () => {
        editor.planet.export(`Planet`);
    }

    const onTakeScreenshot = () => {
        let wereAxesVisible = false;
        if (editor.axes.visible) {
            onToggleAxes();
            wereAxesVisible = true;
        }
        Engine.instance.takeScreenshot(`icosphere_planet.png`);
        if (wereAxesVisible) {
            onToggleAxes();
        }
    }

    const onImportPlanet = () => {
        if (inputFile && inputFile.current) {
            inputFile.current.click();
        }
    }

    const onUploadFile = (e: any) => {
        const files = e.target.files;
        if (files.length > 0) {
            const f = files[0];
            const reader = new FileReader();
            reader.onload = (data => {
                try {
                    if (!editor.loadData(data.target?.result as string)) {
                        alert(`Invalid ${GeoPlanet.FORMAT} format!`);
                    }
                    resetTools();
                } catch (error) {
                    console.log(error)
                    alert('Unexpected upload value.  Please check the file format.')
                }
            });
            reader.readAsText(f);
            e.target.value = null;
        }
    }

    const onSetPaletteColor = (index: number, color: string) => {
        const newColors = [...colorPalette];
        newColors[index] = color;
        setColorPalette(newColors);
        editor.planet.colorPalette.setColors(newColors);
    }

    const onToggleAxes = () => {
        editor.axes.visible = !editor.axes.visible;
    }

    const getToolName = () => {
        if (selectedTool) {
            return selectedTool.name;
        }
        return '';
    }

    const loadTemplate = (data: string) => {
        editor.loadData(data)
        resetTools();
    }

    const renderTopTools = () => {
        return (
            <div id="tool-list">
                <div className={`tool-item ${undoRedoCount[0] > 0 ? '' : 'disabled'}`} onClick={() => editor.undo()}>
                    <svg>
                        <use href='#undo' />
                    </svg>
                    <span className="tooltip-below">Undo</span>
                </div>
                <div className="tool-item" onClick={() => setTemplateModalOpen(true)}>
                    <svg>
                        <use href='#earth-ico' />
                    </svg>
                    <span className="tooltip-below">Templates</span>
                </div>
                <div className={`tool-item ${undoRedoCount[1] > 0 ? '' : 'disabled'}`} onClick={() => editor.redo()}>
                    <svg>
                        <use href='#redo' />
                    </svg>
                    <span className="tooltip-below">Redo</span>
                </div>
            </div>
        )
    }

    const renderTools = () => {
        return (
            <div id="tool-list">
                {Object.values(EditorTools).map(tool =>
                    <div key={tool.name} className={`tool-item ${selectedTool === tool ? 'selected' : ''}`} onClick={() => onSetSelectedTool(tool)}>
                        <svg>
                            <use href={`#${tool.icon}`} />
                        </svg>
                        <span className="tooltip">{tool.description}</span>
                    </div>
                )}
            </div>
        )
    }

    const renderToolTitle = () => {
        if (!selectedTool) {
            return '';
        }
        return (
            <div id="title" onClick={() => setHideToolSettings(!hideToolSettings)}>
                <svg width="24" height="24">
                    <use href={hideToolSettings ? "#up-chevron" : "#down-chevron"}/>
                </svg>
                {getToolName()}
            </div>
        )
    }

    const renderToolInfo = () => {
        if (!selectedTool || hideToolSettings) {
            return '';
        }
        return (
            <>
                {[EditorTools.smooth, EditorTools.raise, EditorTools.lower, EditorTools.level, EditorTools.paint, EditorTools.clear].includes(selectedTool || '') &&
                    <div id="tool-settings">
                        {renderBrushTool()}
                    </div>
                }
                {(selectedTool === EditorTools.water) &&
                    <div id="tool-settings">
                        <div className="tool">
                            <label>Level</label>
                            <input type="range" defaultValue={waterLevel} min="0" max="255" onChange={e => onSetWaterLevel(e.target.value)} />
                        </div>
                        <div className="tool">
                            <label>Density</label>
                            <input type="range" defaultValue={waterDensity} min="0" max="255" onChange={e => onSetWaterDensity(e.target.value)} />
                        </div>
                        <div className="tool">
                            <div className="color-picker">
                                <svg>
                                    <use href="#pencil" />
                                </svg>
                                <input onChange={e => onSetWaterColor(e.target.value)} value={waterColor} type="color" />
                            </div>
                        </div>
                    </div>
                }
                {(selectedTool === EditorTools.atmosphere) &&
                    <div id="tool-settings">
                        <div className="tool">
                            <label>Level</label>
                            <input type="range" defaultValue={atmosphereLevel} min="0" max="255" onChange={e => onSetAtmosphereLevel(e.target.value)} />
                        </div>
                        <div className="tool">
                            <label>Density</label>
                            <input type="range" defaultValue={atmosphereDensity} min="0" max="255" onChange={e => onSeAtmosphereDensity(e.target.value)} />
                        </div>
                        <div className="tool">
                            <div className="color-picker">
                                <svg>
                                    <use href="#pencil" />
                                </svg>
                                <input onChange={e => onSetAtmosphereColor(e.target.value)} value={atmosphereColor} type="color" />
                            </div>
                        </div>
                    </div>
                }
                {(selectedTool === EditorTools.items) &&
                    <div id="scenery-settings">
                        <div id="scenery-sliders">
                            {renderBrushTool()}
                        </div>
                        <div id="scenery-items">
                            {ModelPacks[0].models.sort((a, b) => naturalSort(a.sort, b.sort)).map(model =>
                                <div key={model.id} className={`scenery-item ${selectedObject === model ? 'selected' : ''}`} onClick={() => onSetSelectedObject(model)}>{model.name}</div>
                            )}
                        </div>
                    </div>
                }
                {(selectedTool === EditorTools.settings) &&
                    <div id="tool-settings">
                        <div className="tool-item" onClick={() => onToggleAxes()}>
                            <svg>
                                <use href='#axes' />
                            </svg>
                            <span className="tooltip">Toggle Axes</span>
                        </div>
                        <div key={'download'} className="tool-item" onClick={() => onExportPlanet()}>
                            <svg>
                                <use href='#download' />
                            </svg>
                            <span className="tooltip">Download planet</span>
                        </div>
                        <div key={'upload'} className="tool-item" onClick={() => onImportPlanet()}>
                            <svg>
                                <use href='#upload' />
                            </svg>
                            <span className="tooltip">Upload planet</span>
                            <input ref={inputFile} type='file'
                                accept=".txt"
                                style={{ display: 'none' }}
                                onChange={onUploadFile}
                            />
                        </div>
                        <div key={'screenshot'} className="tool-item" onClick={() => onTakeScreenshot()}>
                            <svg>
                                <use href={'#camera'} />
                            </svg>
                            <span className="tooltip">Take screenshot</span>
                        </div>
                    </div>
                }
            </>
        );
    }

    const renderColors = () => {
        if (!selectedTool || hideToolSettings || ![EditorTools.paint, EditorTools.items].includes(selectedTool)) {
            return '';
        }
        return (
            <div id="tool-colors">
                {colorPalette.map((c, index) =>
                    <div key={index}>
                        <div className={`color-item ${index === selectedColor ? 'selected' : ''}`}
                            style={{ backgroundColor: c }} onClick={() => onSetSelectedColor(index)} />
                    </div>
                )}
                <div className="color-picker">
                    <svg>
                        <use href="#pencil" />
                    </svg>
                    <input onChange={e => onSetPaletteColor(selectedColor, e.target.value)} type="color" value={colorPalette[selectedColor]} />
                </div>
            </div>
        );
    }

    const renderBrushTool = () => {
        return (
            <div className="tool">
                <label>Brush</label>
                <input type="range" value={selectedSize} min="0.0025" max="1.0" step="0.03" onChange={e => onSetSelectedSize(e.target.value)} />
            </div>
        );
    }

    return (
        <>
            <div id="toolbar-component">
                <div id="top-toolbar">
                    <div id="toolbar">
                        {renderTopTools()}
                    </div>
                </div>
                <div id="bottom-toolbar">
                    <div id="toolbar">
                        {renderToolTitle()}
                        {renderColors()}
                        {renderToolInfo()}
                        {renderTools()}
                    </div>
                </div>
            </div>
            {templateModalOpen && <TemplatePlanetModal templates={templatePlanets} onClose={() => setTemplateModalOpen(false)} onSelect={loadTemplate}></TemplatePlanetModal>}
        </>
    );
}

export default Toolbar;
